/* eslint-disable max-lines */
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Link } from '@heidi/design-system/link';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import {
  responseColumnClass,
  responseTurnClass,
  ScrollDownIndicator,
  StreamSlowHint,
  useProseDensity,
} from '@heidi/answer-kit';
import { cn } from '@/lib/cn';
import { useAtomValue, useSetAtom } from 'jotai';
import { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { createResizeObserverWithRAF } from '@/utils/resizeObserverWithRAF';
import {
  TIMELINE_STREAM_ERROR,
  USER_REF_BAN_ERROR,
} from '@/utils/chatErrors';
import {
  IS_INSET_DESIGN_ENABLED,
  IS_SLIDE_BUILDER_ENABLED,
} from '@/features/flags/legacy-flags';
import { useIsVisualDxEnabled, useIsVisualDxModulesEnabled } from '@heidi/evidence/flags';
import type { InteractiveWidgetSubmission } from '@heidi/evidence';
import type { InteractiveWidgetKind } from '@heidi/evidence/contracts';
import type { MessageEntryMethod } from '@/models/analytics/features/chat';
import {
  evidenceSBSChatIdAtom,
  isEvidenceSBSActiveAtom,
} from '@/features/sideBySideEvidence/atoms';
import { QUOTA_UPGRADE_CTA_TEXT } from '../constants/quotaPaywall';
import { visibleChatContent } from '@heidi/integrations/document-inbox';
import type {
  CsvExportItemEntry,
  ExcelExportItemEntry,
  FormFillItemEntry,
  PptxExportItemEntry,
  SessionConversation,
  TextItemEntry,
  TimelineEntry,
} from '../types/types';
import { ChatMode } from '../types/types';
import { presentationAtom } from '@/atoms/slideBuilderAtoms';
import { logger } from '@/utils/logger/logger';
import { presentationPreviewAtom } from '@/components/slide-builder/presentationPreviewAtom';
import { artefactPreviewAtom } from '@heidi/evidence';
import { AppointmentsBlock } from './AppointmentsBlock';
import { ChatQuestionNav } from './ChatQuestionNav';
import { ClinicalTaskResultBlock } from './ClinicalTaskResultBlock';
import {
  ArtefactBlock,
  CalculatorBlock,
  CollapsedSourcesSummary,
  EmailPatientEvidenceBlock,
  FormulaCalculatorBlock,
  InlinePatientSummaryBlock,
  InlineSearchBlock,
  LibrarySearchBlock,
  PrintDocumentBlock,
} from '@heidi/evidence';
import { EvidenceAnswerPortsHost } from '@/composition/hosts/evidence-answer-ports-host';
import { UserMessage } from '@/components/conversation/UserMessage';
import { ComplianceErrorMessage, TextResponse } from './components';
import { FormFillNeedsTemplateBlock } from './FormFillNeedsTemplateBlock';
import { InlineReasoningBlock } from './InlineReasoningBlock';
import { InteractiveWidgetBlock } from './InteractiveWidgetBlock';
import { PatientSearchBlock } from './PatientSearchBlock';
import { ProcessingBlock, type ProcessingType } from './ProcessingBlock';
import { ProtocolToolBlock } from './ProtocolToolBlock';
import { QuotaExceededMessage } from './QuotaExceededCTA';
import { VisualDxBlock } from './VisualDxBlock';
import { ReplyActions } from './ReplyActions';
import { SearchGroupTransition } from '@heidi/design-system/search-group-transition';
import { SessionsByDateBlock } from './SessionsByDateBlock';
import { StreamRecoveryIndicator } from './StreamRecoveryIndicator';
import { CreateTemplateBlock } from './CreateTemplateBlock';
import { EditTemplateSectionBlock } from './EditTemplateSectionBlock';
import { TemplateSearchBlock } from './TemplateSearchBlock';
import { ThinkingBlock } from './ThinkingBlock';
import { TierRestrictedToolBlock } from './TierRestrictedToolBlock';
import { UnauthenticatedToolAccessErrorBlock } from './UnauthenticatedToolAccessErrorBlock';
import { UnsupportedToolBlock } from './UnsupportedToolBlock';
import { EhrToolBlock } from './EhrToolBlock';
import { RateLimitErrorBlock } from './RateLimitErrorBlock';

const VISUALDX_MODULE_WIDGET_KINDS: ReadonlySet<InteractiveWidgetKind> = new Set([
  'visualdx_gallery',
  'visualdx_compare',
  'visualdx_drug_ddx',
  'visualdx_findings',
  'visualdx_built_differential',
  'visualdx_handout',
]);

const PROCESSING_TYPE_KEYWORDS: readonly { type: ProcessingType; keywords: readonly string[]; matchAll?: readonly string[] }[] = [
  { type: 'pptx', keywords: ['powerpoint', 'power point', 'pptx', 'presentation', 'slides', 'slide deck'] },
  { type: 'pdf', keywords: ['pdf', 'document', 'export', 'download', 'spreadsheet', 'csv', 'excel'] },
  { type: 'patient_pdf', keywords: ['patient'], matchAll: ['handout', 'evidence'] },
  { type: 'email', keywords: ['email', 'send'] },
  { type: 'table', keywords: ['table', 'compare', 'list'] },
] as const;

const UserRefBannedBlock = ({
  isAuthenticated,
}: {
  isAuthenticated: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const getSupportLabel = isAuthenticated
    ? intl.formatMessage({
        defaultMessage: 'Get support',
        description:
          'Help menu item label interpolated into the Evidence account-ban message; must match the sidebar Help item',
        id: 'chat.userRefBan.getSupport',
      })
    : '';

  return (
    <div className="w-full rounded-lg border border-border bg-surface p-4">
      <TypographyP2 className="text-text-primary">
        {isAuthenticated ? (
          <FormattedMessage
            defaultMessage="Unusual activity has been detected in your account, so answers in Evidence have been disabled. If you believe this is in error, please click on (?) Help, select ''{getSupport}'' and send us a message."
            description="Evidence chat banner when the account cannot ask questions. {getSupport} is the Help menu item in the active UI language."
            id="chat.userRefBan.authenticatedMessage"
            values={{ getSupport: getSupportLabel }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Unusual activity has been detected in your account, so answers in Evidence have been disabled. If you believe this is in error, please send an email to {contactHeidiLink} ({contactEmail})"
            description="Logged-out Evidence chat banner when the account cannot ask questions. {contactHeidiLink} is the support mailto and {contactEmail} is the same address in plain text so it can be copied."
            id="chat.userRefBan.unauthenticatedMessage"
            values={{
              contactEmail: USER_REF_BAN_ERROR.CONTACT_EMAIL,
              contactHeidiLink: (
                <Link href={USER_REF_BAN_ERROR.CONTACT_URL} variant="inline">
                  <FormattedMessage
                    defaultMessage="Customer support"
                    description="Mailto link text in the logged-out Evidence account-ban message"
                    id="chat.userRefBan.customerSupport"
                  />
                </Link>
              ),
            }}
          />
        )}
      </TypographyP2>
    </div>
  );
};

export function inferProcessingType(userQuery: string): ProcessingType {
  for (const rule of PROCESSING_TYPE_KEYWORDS) {
    if (rule.matchAll) {
      if (rule.keywords.some((k) => userQuery.includes(k)) && rule.matchAll.some((k) => userQuery.includes(k))) {
        return rule.type;
      }
    } else if (rule.keywords.some((k) => userQuery.includes(k))) {
      return rule.type;
    }
  }
  return 'general';
}

/**
 * Creates an onPreview handler for artefact blocks that opens the ArtefactPreviewModal.
 * Extracted to a shared helper to eliminate duplication across artefact render sites.
 */
export const createArtefactPreviewHandler = (
  setArtefactPreview: (state: {
    isOpen: boolean;
    url: string;
    filename: string;
    title: string;
  }) => void,
  downloadUrl: string | undefined,
  filename: string,
  title: string
): (() => void) => {
  return () => {
    if (downloadUrl) {
      setArtefactPreview({
        isOpen: true,
        url: downloadUrl,
        filename,
        title,
      });
    }
  };
};

interface ChatTimelineProps {
  sessionId: string | null;
  conversation: SessionConversation;
  isStreaming: boolean;
  isRecovering?: boolean;
  latestGroupRef: React.RefObject<HTMLDivElement>;
  timelineRef: React.RefObject<HTMLDivElement>;
  /** Optional viewport ref for scroll listeners / measurements */
  scrollViewportRef?: React.MutableRefObject<HTMLDivElement | null>;
  onRetryLastMessage?: () => void;
  onTryWithResearch?: () => void;
  hasMoreContentBelow?: boolean;
  onScrollToBottom?: () => void;
  /** Whether the chat is displayed in full-screen mode (vs sidebar) */
  isFullScreenEvidence?: boolean;
  /** Chat ID for follow-up questions feature */
  chatId?: string;
  /** Chat title from backend metadata (streamed during response) */
  chatTitle?: string;
  /** User's specialty for more relevant follow-up suggestions */
  specialty?: string;
  /** Callback when a follow-up question is selected */
  onSelectFollowUp?: (question: string, entryMethod?: MessageEntryMethod) => void;
  /** Called before sending a follow-up from Refine sources; aborts any active stream */
  onAbortStream?: () => void;
  /** Callback to share the chat */
  onShare?: () => void;
  /**
   * Invoked when an inline interactive widget (e.g. the VisualDx
   * diagnostic flow) signals submission. Forwarded straight to each
   * ``InteractiveWidgetBlock`` so the widget can hand its typed
   * selections back to the chat for formatting + dispatch as the next
   * user turn.
   */
  onInteractiveWidgetSubmit?: (
    submission: InteractiveWidgetSubmission
  ) => void;
  /** Whether the current chat user is authenticated. */
  isAuthenticated?: boolean;
  /** Epoch ms until a rate-limit cooldown ends. Null when send is allowed. */
  rateLimitCooldownUntil?: number | null;
  /**
   * True only on surfaces that mount `PresentationPanel` (`ChatWithCanvas`).
   * In-session Ask Heidi, `/chat/embed`, SBS, and share have no panel, so PPTX
   * preview must fall back to the artefact modal — the same path PDF already
   * uses (EVI-2093).
   */
  canOpenPresentationPanel?: boolean;
}

// OPTIMIZATION: Wrapped in memo to prevent unnecessary re-renders when parent
// re-renders but props haven't changed (e.g., during streaming state updates)
export const ChatTimeline = memo(
  forwardRef<HTMLDivElement, ChatTimelineProps>(function ChatTimeline(
    {
      conversation,
      isStreaming,
      isRecovering = false,
      latestGroupRef,
      timelineRef,
      scrollViewportRef,
      onRetryLastMessage,
      onTryWithResearch,
      sessionId,
      hasMoreContentBelow = false,
      onScrollToBottom,
      isFullScreenEvidence = false,
      chatId,
      chatTitle,
      specialty,
      onSelectFollowUp,
      onAbortStream,
      onShare,
      onInteractiveWidgetSubmit,
      isAuthenticated = true,
      canOpenPresentationPanel = false,
      rateLimitCooldownUntil = null,
    },
    ref
  ) {
    const isMobile = useIsMobile();
    // The thread's vertical rhythm scales with the surface the same way the
    // prose does, so a sidebar groups a turn as tightly as it sets one.
    // `Chat` publishes this; the fallback covers an embedder that has not.
    const density = useProseDensity(isFullScreenEvidence ? 'comfortable' : 'compact');
    const setArtefactPreview = useSetAtom(artefactPreviewAtom);
    const setPresentationPreview = useSetAtom(presentationPreviewAtom);
    const currentPresentation = useAtomValue(presentationAtom);
    // EVI-1602: while the side-by-side Evidence overlay is up, the user's
    // query shows through above the overlay heading. Tighten the first
    // message's top spacing for that chat only. The chat id atom is still
    // null during `/chat/sbs/setup`, so treat null as "the current chat".
    const isSBSActiveGlobal = useAtomValue(isEvidenceSBSActiveAtom);
    const sbsChatId = useAtomValue(evidenceSBSChatIdAtom);
    const isSBSActive =
      isSBSActiveGlobal && (sbsChatId === null || sbsChatId === chatId);
    // EVI-1349: defense-in-depth gate. The BE flag of the same name already
    // prevents `interactive_widget` chunks from being emitted when off; this
    // hook stops the FE from rendering anything if a stale or replayed chunk
    // somehow lands in the timeline.
    const isVisualDxEnabled = useIsVisualDxEnabled();
    const isVisualDxModulesEnabled = useIsVisualDxModulesEnabled();

    /** Shorthand: creates an onPreview handler bound to setArtefactPreview */
    const openArtefactPreview = (
      downloadUrl: string | undefined,
      filename: string,
      title: string
    ) => createArtefactPreviewHandler(setArtefactPreview, downloadUrl, filename, title);

    /** Shared render helper for CSV/Excel export artefact blocks */
    const renderSpreadsheetExportBlock = (
      key: string,
      entry: CsvExportItemEntry | ExcelExportItemEntry
    ) => {
      const isCsv = entry.type === 'csv_export_item';
      const defaultLabel = isCsv ? 'CSV Export' : 'Excel Export';
      const defaultFilename = isCsv ? 'export.csv' : 'export.xlsx';
      return (
        <ArtefactBlock
          key={key}
          artefactType={isCsv ? 'csv' : 'excel'}
          status={entry.status}
          title={entry.filename || defaultLabel}
          url={entry.downloadUrl}
          error={entry.error}
          showMetadata={!!entry.rowCount}
          metadataDescription={entry.rowCount ? `${entry.rowCount} rows` : undefined}
          onPreview={openArtefactPreview(
            entry.downloadUrl,
            entry.filename || defaultFilename,
            entry.filename || defaultLabel
          )}
        />
      );
    };

    /** Shared render helper for PowerPoint export artefact blocks.
     *
     * `PresentationPanel` is only mounted by `ChatWithCanvas`. Every other
     * ChatTimeline host (in-session, embed, SBS, share) falls back to the
     * artefact modal — the same path PDF already uses (EVI-2093). */
    const renderPptxExportBlock = (key: string, entry: PptxExportItemEntry) => {
      const previewUrl = entry.previewUrl || entry.downloadUrl;
      const previewFilename = entry.previewUrl ? 'preview.pdf' : (entry.filename || 'presentation.pptx');
      const liveSlideCount =
        entry.presentationId && currentPresentation?.id === entry.presentationId
          ? currentPresentation.slides.length
          : entry.slideCount;
      const openInPreviewModal = openArtefactPreview(
        previewUrl,
        previewFilename,
        entry.filename || 'PowerPoint Export'
      );
      const openPresentationPanel =
        IS_SLIDE_BUILDER_ENABLED() && canOpenPresentationPanel;
      const handlePptxPreview = () => {
        if (openPresentationPanel) {
          logger.info('[SlideBuilder] ChatTimeline opening presentation panel', {
            presentationId: entry.presentationId ?? null,
            url: entry.downloadUrl,
          });
          setPresentationPreview({
            isOpen: true,
            presentationId: entry.presentationId ?? null,
          });
          return;
        }
        openInPreviewModal();
      };
      return (
        <ArtefactBlock
          key={key}
          artefactType="pptx"
          status={entry.status}
          title={entry.filename || 'PowerPoint Export'}
          url={entry.downloadUrl}
          error={entry.error}
          showMetadata={!!liveSlideCount}
          metadataDescription={liveSlideCount ? `${liveSlideCount} slides` : undefined}
          onPreview={handlePptxPreview}
        />
      );
    };

    /** Shared render helper for form-fill timeline entries (both needs_template and artefact). */
    const renderFormFillBlock = (key: string, entry: FormFillItemEntry) => {
      if (entry.status === 'needs_template') {
        const isActioned = conversation.timeline.some(
          (e) => e.type === 'form_fill_item' && e.status !== 'needs_template' && e.position > entry.position
        );
        return <FormFillNeedsTemplateBlock key={key} title={entry.title} isActioned={isActioned} />;
      }
      const formFillStatus = entry.status === 'filling' ? 'generating' : entry.status;
      return (
        <ArtefactBlock
          key={key}
          artefactType="form_fill"
          status={formFillStatus}
          title={entry.title}
          url={entry.downloadUrl}
          error={entry.error}
          showMetadata={!!entry.filename}
          metadataDescription={entry.filename}
          onPreview={openArtefactPreview(entry.downloadUrl, entry.filename || 'filled_form.pdf', entry.title)}
        />
      );
    };

    // Track scroll container height for sidebar mode minHeight calculation
    // In sidebar mode, percentage heights don't work because there's no explicit parent height
    // TODO: Fix in-session evidence scroll animation - the minHeight approach below isn't working
    // correctly for the sidebar. When a follow-up query is sent, content should scroll up and
    // the new query should appear at the top (like out-of-session evidence does).
    // See: useAutoScroll hook for the scroll trigger logic.
    const [containerHeight, setContainerHeight] = useState<number | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    // Callback ref for the scroll viewport - sets up ResizeObserver when element is attached
    const viewportRefCallback = useCallback(
      (node: HTMLDivElement | null) => {
        // Always expose the viewport node for parent consumers (e.g. scroll listeners),
        // even when we skip measurement for full-screen mode.
        if (scrollViewportRef) {
          scrollViewportRef.current = node;
        }

        // Clean up previous observer
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }

        // Skip measurement for full-screen mode (uses 100dvh instead)
        if (isFullScreenEvidence || !node) {
          setContainerHeight(null);
          return;
        }

        // Initial measurement
        setContainerHeight(node.clientHeight);

        // Set up ResizeObserver for dynamic height tracking
        resizeObserverRef.current = createResizeObserverWithRAF(() => {
          setContainerHeight(node.clientHeight);
        });
        resizeObserverRef.current.observe(node);
      },
      [isFullScreenEvidence, scrollViewportRef]
    );

    // Clean up ResizeObserver on unmount
    useEffect(() => {
      return () => {
        resizeObserverRef.current?.disconnect();
      };
    }, []);


    // Find the position of the last text_item entry (most recent AI response)
    const lastTextItemPosition = conversation.timeline
      .slice()
      .reverse()
      .find((entry) => entry.type === 'text_item')?.position;

    // Find the position of the first user_message entry for branded styling
    const firstUserMessagePosition = conversation.timeline.find(
      (entry) => entry.type === 'user_message'
    )?.position;

    // EVI-1567 / EV-023: positions of the last text_item in each answer, used to
    // render the drug-interaction disclaimer exactly once per answer, at the end
    // of the answer's text (before its references). A user_message marks the
    // start of a new answer, so the text_items seen since the previous
    // user_message belong to that answer. This is derived purely from
    // user-message boundaries and positions — it does NOT re-derive the V2
    // renderer's group index, so it cannot drift from the renderer (e.g. a
    // leading non-rendering entry can never shift the result and silently
    // suppress this regulatory disclaimer).
    const answerEndingTextPositions = (() => {
      const positions = new Set<number>();
      let lastTextPositionInAnswer: number | undefined;
      for (const entry of conversation.timeline) {
        if (entry.type === 'user_message') {
          if (lastTextPositionInAnswer !== undefined) {
            positions.add(lastTextPositionInAnswer);
          }
          lastTextPositionInAnswer = undefined;
          continue;
        }
        if (entry.type === 'text_item') lastTextPositionInAnswer = entry.position;
      }
      if (lastTextPositionInAnswer !== undefined) {
        positions.add(lastTextPositionInAnswer);
      }
      return positions;
    })();

    // ===== V2 TIMELINE HELPER TYPES =====
    interface ReplyMetadata {
      mergedContent: string;
      lastPosition: number;
      mode: ChatMode | undefined;
    }

    // ===== V2 TIMELINE HELPER FUNCTIONS =====

    /** Check if entry is an activity block (reasoning, web search, library search, patient summary, capability tool, interactive widget, or thinking) */
    const isActivityEntry = (entry: TimelineEntry): boolean =>
      entry.type === 'reasoning_item' ||
      entry.type === 'web_search_item' ||
      entry.type === 'library_search_item' ||
      entry.type === 'patient_summary_item' ||
      entry.type === 'capability_tool_item' ||
      entry.type === 'visualdx_item' ||
      entry.type === 'interactive_widget_item' ||
      entry.type === 'thinking_item';

    /** Check if entry is actively streaming */
    const isActiveEntry = (entry: TimelineEntry): boolean => {
      if (!('backendIndex' in entry)) return false;
      const { streamingState } = conversation;
      return (
        streamingState?.currentActiveIndex === entry.backendIndex &&
        entry.position >= (streamingState?.basePosition ?? 0)
      );
    };

    /** Get position label for grouped items */
    const getGroupPosition = (
      idx: number,
      length: number
    ): 'first' | 'middle' | 'last' | 'only' => {
      if (length === 1) return 'only';
      if (idx === 0) return 'first';
      if (idx === length - 1) return 'last';
      return 'middle';
    };

    /** Check if entry is a tool output block (breaks text groups for proper reference placement) */
    const isToolOutputEntry = (entry: TimelineEntry): boolean =>
      entry.type === 'pdf_export_item' ||
      entry.type === 'print_document_item' ||
      entry.type === 'patient_evidence_pdf_item' ||
      entry.type === 'form_fill_item' ||
      entry.type === 'email_patient_evidence_item' ||
      entry.type === 'calculator_item' ||
      entry.type === 'formula_calculator_item' ||
      entry.type === 'sessions_by_date_item' ||
      entry.type === 'csv_export_item' ||
      entry.type === 'word_export_item' ||
      entry.type === 'excel_export_item' ||
      entry.type === 'pptx_export_item' ||
      entry.type === 'appointments_item' ||
      entry.type === 'search_templates_item' ||
      entry.type === 'search_community_templates_item' ||
      entry.type === 'search_my_templates_item' ||
      entry.type === 'get_template_prefs_item' ||
      entry.type === 'create_template_item' ||
      entry.type === 'edit_template_section_item' ||
      entry.type === 'search_patient_profiles_item';

    /** Group consecutive text blocks - activity/user messages/tool outputs break groups */
    const groupConsecutiveTextBlocks = (): Map<number, TextItemEntry[]> => {
      const groups = new Map<number, TextItemEntry[]>();
      let currentGroup: TextItemEntry[] = [];
      let groupStartPosition: number | null = null;
      const { timeline } = conversation;

      const flushGroup = () => {
        if (currentGroup.length > 0 && groupStartPosition !== null) {
          groups.set(groupStartPosition, [...currentGroup]);
          currentGroup = [];
          groupStartPosition = null;
        }
      };

      timeline.forEach((entry) => {
        if (entry.type === 'text_item') {
          if (groupStartPosition === null) groupStartPosition = entry.position;
          currentGroup.push(entry);
        } else if (
          isActivityEntry(entry) ||
          entry.type === 'user_message' ||
          isToolOutputEntry(entry)
        ) {
          flushGroup();
        }
      });

      flushGroup();
      return groups;
    };

    /** Render a group of consecutive web search blocks */
    const renderSearchGroup = (
      searchGroup: TimelineEntry[],
      currentGroupIdx: number,
      conversationGroups: JSX.Element[][],
      waitingForText = false,
    ) => {
      const firstEntry = searchGroup[0];
      if (!firstEntry) return;

      const allSearchesComplete = searchGroup.every(
        (e) => e.type === 'web_search_item' && e.status === 'completed'
      );

      const sharedKey = `search-group-${firstEntry.position}`;

      const searchElements = searchGroup.map((searchEntry, idx) => {
        if (searchEntry.type !== 'web_search_item') return null;
        return (
          <InlineSearchBlock
            key={`search-${searchEntry.position}`}
            query={searchEntry.query || ''}
            sources={searchEntry.sources}
            status={searchEntry.status}
            groupPosition={getGroupPosition(idx, searchGroup.length)}
            // EVI-1486: thread chat/message/session IDs into per-row click tracking.
            // Prefer backend `msg_*` id with fallback to frontend entry id (see
            // the non-grouped branch above for the rationale on the fallback).
            chatId={chatId}
            messageId={searchEntry.messageId ?? searchEntry.id}
            sessionId={sessionId}
          />
        );
      });

      const searchBlocksContent = (
        <div className="flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border">
          {searchElements}
        </div>
      );

      const allSources = searchGroup.flatMap((e) =>
        e.type === 'web_search_item' ? e.sources : []
      );

      // Include waitingForText in the key so that when text arrives, the
      // component remounts with allComplete=true and jumps straight to the
      // "found X sources" summary without a fade-out animation on the
      // search rows the user never saw in completed state.
      conversationGroups[currentGroupIdx]?.push(
        <SearchGroupTransition
          key={`${sharedKey}-${waitingForText}`}
          allComplete={!waitingForText && allSearchesComplete}
          searchBlocks={searchBlocksContent}
          summary={
            <CollapsedSourcesSummary
              sources={allSources}
              // EVI-1486: thread chat/session IDs into the aggregated rollup so
              // every row click emits a fully-joinable evidence_source_clicked.
              // Prefer the backend `msg_*` id stamped on the first search entry
              // (every sub-agent search in a turn shares the same parent
              // message); fall back to the frontend entry id only if the
              // backend id hasn't been captured yet.
              chatId={chatId}
              messageId={
                firstEntry?.type === 'web_search_item'
                  ? firstEntry.messageId ?? firstEntry.id
                  : undefined
              }
              sessionId={sessionId}
            />
          }
        />
      );
    };

    /** Render a merged text group */
    const renderTextGroup = (
      groupStart: number,
      groupEntries: TextItemEntry[],
      currentGroupIdx: number,
      conversationGroups: JSX.Element[][],
      replyMetadata: ReplyMetadata[]
    ) => {
      const mergedContent = groupEntries.map((e) => e.content).join('');
      const lastEntry = groupEntries.at(-1);
      if (!lastEntry) return;

      const isLastTextInResponse = lastEntry.position === lastTextItemPosition;

      // Track merged content and mode for ReplyActions
      if (replyMetadata[currentGroupIdx]) {
        replyMetadata[currentGroupIdx].mergedContent += mergedContent;
        replyMetadata[currentGroupIdx].mode = lastEntry.mode;
      }

      // EVI-1567 / EV-023: this text group is the last in its answer when its
      // last entry is the answer's last text_item. TextResponse renders the
      // drug-interaction banner here (before the references section).
      const isLastTextGroupInAnswer = answerEndingTextPositions.has(
        lastEntry.position
      );

      const isQuotaPaywall =
        groupEntries.some((e) => e.quotaExceeded) ||
        mergedContent.includes(QUOTA_UPGRADE_CTA_TEXT);

      if (isQuotaPaywall) {
        conversationGroups[currentGroupIdx]?.push(
          <div key={`text-group-${groupStart}`} className="pb-5">
            <QuotaExceededMessage content={mergedContent} />
          </div>
        );
        return;
      }

      const isAnyStreaming = groupEntries.some((e) => isActiveEntry(e));

      conversationGroups[currentGroupIdx]?.push(
        <TextResponse
          key={`text-group-${groupStart}`}
          sessionId={sessionId}
          chunks={groupEntries.map((e) => ({
            type: 'text' as const,
            text: e.content,
            index: e.backendIndex,
            annotations: e.annotations,
            citations: e.citations,
            groupId: e.groupId,
            id: e.id,
            drugReferenced: e.drugReferenced,
          }))}
          conversation={conversation}
          position={lastEntry.position}
          isStreaming={isAnyStreaming && isStreaming}
          onTryWithResearch={onTryWithResearch}
          mode={lastEntry.mode}
          isLastTextResponse={isLastTextInResponse}
          isLastInTextGroup={false}
          isLastTextGroupInAnswer={isLastTextGroupInAnswer}
          groupContent={mergedContent}
          isFullScreenEvidence={isFullScreenEvidence}
          chatTitle={chatTitle}
        />
      );
    };

    /** Build final conversation group elements with ReplyActions */
    const buildFinalElements = (
      conversationGroups: JSX.Element[][],
      replyMetadata: ReplyMetadata[]
    ): JSX.Element[] => {
      const elements: JSX.Element[] = [];

      conversationGroups.forEach((group, idx) => {
        if (group.length === 0) return;

        const isLastGroup = idx === conversationGroups.length - 1;
        const metadata = replyMetadata[idx];

        const hasAIBlocks = group.some(
          (el) =>
            el.key?.toString().startsWith('reasoning-') ||
            el.key?.toString().startsWith('search-') ||
            el.key?.toString().startsWith('text-')
        );

        const groupIsStreaming = isLastGroup && isStreaming;

        // EVI-608: Hide action buttons (not CSAT) when user notes are present
        // (copy/insert actions are shown in the UserNotesBlock instead)
        const hasUserNotes =
          metadata?.mergedContent?.includes('<usernotes>') ||
          metadata?.mergedContent?.includes('<usernotes ');

        if (isLastGroup && isRecovering) {
          group.push(<StreamRecoveryIndicator key="stream-recovery-inline" />);
        }

        if (hasAIBlocks && !groupIsStreaming && !isRecovering && metadata) {
          group.push(
            <ReplyActions
              key={`reply-actions-${idx}`}
              sessionId={sessionId}
              conversation={conversation}
              position={metadata.lastPosition}
              mode={metadata.mode ?? ChatMode.EVIDENCE}
              actionableContent={metadata.mergedContent}
              onShare={onShare}
              chatId={chatId}
              chatTitle={chatTitle}
              specialty={specialty}
              isStreaming={isStreaming}
              onSelectFollowUp={onSelectFollowUp}
              isLastTextResponse={isLastGroup}
              hideActionButtons={hasUserNotes}
            />
          );
        }

        elements.push(
          <div
            key={`v2-conversation-group-${idx}`}
            ref={isLastGroup ? latestGroupRef : undefined}
            data-testid="evidence-response-group"
            // One turn. The question and every part of the answer are direct
            // children here, which is what lets the question-to-answer gap be
            // an adjacent-sibling rule rather than a margin either side owns.
            className={cn('min-w-0 overflow-x-hidden', responseTurnClass(density))}
            style={
              // minHeight ensures the last conversation group fills container so user message
              // scrolls to top when auto-scroll triggers on follow-up queries.
              // Full-screen: use viewport height minus header/input offsets
              // Sidebar: use measured container height (percentage doesn't work in scroll context)
              isLastGroup
                ? {
                    minHeight: isFullScreenEvidence
                      ? `calc(100dvh - ${isMobile ? '170px' : '210px'})`
                      : containerHeight
                        ? `${containerHeight}px`
                        : undefined,
                  }
                : undefined
            }
          >
            {group}
          </div>
        );
      });

      return elements;
    };

    /**
     * V2 Block-Based Timeline Rendering
     *
     * Each block type (Text, Search, Reasoning) renders independently as inline
     * stackable components. No ActivityBox wrapper - supports any sequence pattern.
     */
    const renderV2Timeline = (): JSX.Element[] => {
      const { timeline } = conversation;

      const v2TextGroups = groupConsecutiveTextBlocks();
      const renderedTextPositions = new Set<number>();
      const renderedSearchPositions = new Set<number>();

      const conversationGroups: JSX.Element[][] = [[]];
      let currentGroupIdx = 0;
      /** User message content for the current group; used by ThinkingBlock "Refine sources" */
      let lastUserMessageContentInGroup: string | undefined;

      const replyMetadata: ReplyMetadata[] = [
        { mergedContent: '', lastPosition: 0, mode: undefined },
      ];

      // Process each timeline entry
      for (const entry of timeline) {
        if (entry.type === 'error_item') continue;

        // User message starts a new conversation group
        if (entry.type === 'user_message') {
          const shownQuestion = visibleChatContent(entry.content);
          lastUserMessageContentInGroup = shownQuestion;
          if (conversationGroups[currentGroupIdx]?.length) {
            currentGroupIdx++;
            conversationGroups[currentGroupIdx] = [];
            replyMetadata.push({ mergedContent: '', lastPosition: 0, mode: undefined });
          }
          conversationGroups[currentGroupIdx]?.push(
            <UserMessage
              key={`user-${entry.position}`}
              message={shownQuestion}
              files={entry.files}
              messageId={entry.id}
              isFirstUserMessage={entry.position === firstUserMessagePosition}
              isFullScreenEvidence={isFullScreenEvidence}
            />
          );
          continue;
        }

        // Track position for ReplyActions
        const currentMetadata = replyMetadata[currentGroupIdx];
        if (currentMetadata) {
          currentMetadata.lastPosition = entry.position;
        }

        // Handle web search entries
        if (entry.type === 'web_search_item') {
          if (renderedSearchPositions.has(entry.position)) continue;

          const searchGroup: TimelineEntry[] = [entry];
          const entryIdx = timeline.indexOf(entry);

          for (let i = entryIdx + 1; i < timeline.length; i++) {
            const nextEntry = timeline[i];
            if (nextEntry?.type === 'web_search_item') {
              searchGroup.push(nextEntry);
            } else {
              break;
            }
          }

          // Keep showing "searching" animation until text starts streaming.
          // Without this, completed searches sit idle while the model
          // generates text, making users think the response is stuck.
          const hasTextAfterSearches = timeline.some(
            (e, i) => i > entryIdx + searchGroup.length - 1 && e.type === 'text_item'
          );
          const waitingForText = isStreaming && !hasTextAfterSearches;

          searchGroup.forEach((e) => renderedSearchPositions.add(e.position));
          renderSearchGroup(searchGroup, currentGroupIdx, conversationGroups, waitingForText);
          continue;
        }

        // Handle library search entries (user's uploaded documents)
        // LibrarySearchBlock has its own card styling (bg-background-tertiary)
        if (entry.type === 'library_search_item') {
          conversationGroups[currentGroupIdx]?.push(
            <LibrarySearchBlock
              key={`library-search-${entry.position}`}
              query={entry.query || ''}
              sources={entry.sources}
              status={entry.status}
            />
          );
          continue;
        }

        // EVI-591: Handle patient summary (pre-chart) entries
        if (entry.type === 'patient_summary_item') {
          conversationGroups[currentGroupIdx]?.push(
            <InlinePatientSummaryBlock
              key={`patient-summary-${entry.position}`}
              status={entry.status}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle protocol capability tool entries (e.g., get_patient_summary, execute_template_workflow)
        if (entry.type === 'capability_tool_item') {
          conversationGroups[currentGroupIdx]?.push(
            <ProtocolToolBlock
              key={`capability-tool-${entry.position}`}
              name={entry.name}
              status={entry.status}
              error={entry.error}
            />
          );
          continue;
        }

        if (entry.type === 'visualdx_item') {
          conversationGroups[currentGroupIdx]?.push(
            <VisualDxBlock
              key={`visualdx-${entry.position}`}
              tool={entry.tool}
              status={entry.status}
              query={entry.query}
              error={entry.error}
              diagnoses={entry.diagnoses}
              compare_url={entry.compare_url}
              detail={entry.detail}
              onDiagnosisSelect={
                isVisualDxEnabled && onSelectFollowUp
                  ? (name) =>
                      onSelectFollowUp(
                        `Show VisualDx images and clinical details for ${name}`
                      )
                  : undefined
              }
            />
          );
          continue;
        }

        if (entry.type === 'interactive_widget_item') {
          if (!isVisualDxEnabled) {
            continue;
          }
          if (!isVisualDxModulesEnabled && VISUALDX_MODULE_WIDGET_KINDS.has(entry.kind)) {
            continue;
          }
          conversationGroups[currentGroupIdx]?.push(
            <InteractiveWidgetBlock
              key={`interactive-widget-${entry.position}`}
              kind={entry.kind}
              status={entry.status}
              query={entry.query}
              error={entry.error}
              differential={entry.differential}
              gallery={entry.gallery}
              compare={entry.compare}
              drugReaction={entry.drugReaction}
              findings={entry.findings}
              builtDifferential={entry.builtDifferential}
              handout={entry.handout}
              onSubmit={onInteractiveWidgetSubmit}
            />
          );
          continue;
        }

        // Handle reasoning entries
        if (entry.type === 'reasoning_item') {
          // Check if this is the placeholder entry (backendIndex: -1)
          // Render ThinkingBlock with 'thinking' status to match what thinking_item will render
          // This ensures same component type + same key = no remount when thinking_item arrives
          if (entry.backendIndex === -1) {
            const hasThinkingItem = timeline.some((e) => e.type === 'thinking_item');
            if (!hasThinkingItem) {
              conversationGroups[currentGroupIdx]?.push(
                <ThinkingBlock
                  key={`loading-${entry.position}`}
                  status="thinking"
                  relatedUserQuery={lastUserMessageContentInGroup ?? ''}
                  onSelectFollowUp={onSelectFollowUp}
                  onAbortStream={onAbortStream}
                />
              );
            }
            continue;
          }

          conversationGroups[currentGroupIdx]?.push(
            <InlineReasoningBlock
              key={`reasoning-${entry.position}`}
              content={entry.content}
              isStreaming={isActiveEntry(entry) && isStreaming}
            />
          );
          continue;
        }

        // EVI-334: Handle thinking entries (extended thinking mode)
        // Use 'loading-' key prefix to match placeholder key and prevent remount
        if (entry.type === 'thinking_item') {
          conversationGroups[currentGroupIdx]?.push(
            <ThinkingBlock
              key={`loading-${entry.position}`}
              status={entry.status}
              durationMs={entry.durationMs}
              relatedUserQuery={lastUserMessageContentInGroup ?? ''}
              onSelectFollowUp={onSelectFollowUp}
              onAbortStream={onAbortStream}
            />
          );
          continue;
        }

        // Handle PDF export entries
        if (entry.type === 'pdf_export_item') {
          conversationGroups[currentGroupIdx]?.push(
            <ArtefactBlock
              key={`artefact-${entry.position}`}
              artefactType="pdf"
              status={entry.status}
              title={entry.title}
              url={entry.downloadUrl}
              showMetadata={!!entry.filename}
              metadataDescription={entry.filename}
              onPreview={openArtefactPreview(entry.downloadUrl, entry.filename || 'document.pdf', entry.title)}
            />
          );
          continue;
        }

        // Handle print-document entries (web-only). First-time print is
        // auto-fired on the stream; the Print button here lets the user
        // re-open the dialog, including after chat reload from persisted
        // additional_metadata.print_documents.
        if (entry.type === 'print_document_item') {
          conversationGroups[currentGroupIdx]?.push(
            <PrintDocumentBlock
              key={`artefact-${entry.position}`}
              toolId={entry.toolId}
              status={entry.status}
              title={entry.title}
              url={entry.downloadUrl}
              filename={entry.filename}
              error={entry.error}
              onPreview={openArtefactPreview(entry.downloadUrl, entry.filename || 'document.pdf', entry.title)}
            />
          );
          continue;
        }

        // Handle patient evidence PDF entries
        if (entry.type === 'patient_evidence_pdf_item') {
          conversationGroups[currentGroupIdx]?.push(
            <ArtefactBlock
              key={`artefact-${entry.position}`}
              artefactType="patient_handout"
              status={entry.status}
              title={entry.title}
              url={entry.downloadUrl}
              error={entry.error}
              showMetadata={!!entry.conditionsIncluded || !!entry.filename}
              metadataDescription={[
                entry.filename,
                entry.conditionsIncluded ? `${entry.conditionsIncluded} conditions included` : undefined,
              ].filter(Boolean).join(' • ')}
              onPreview={openArtefactPreview(entry.downloadUrl, entry.filename || 'patient_handout.pdf', entry.title)}
            />
          );
          continue;
        }

        // Handle form fill entries
        if (entry.type === 'form_fill_item') {
          const formKey = entry.status === 'needs_template'
            ? `form-fill-needs-template-${entry.position}`
            : `artefact-${entry.position}`;
          conversationGroups[currentGroupIdx]?.push(renderFormFillBlock(formKey, entry));
          continue;
        }

        // Handle email patient evidence entries
        if (entry.type === 'email_patient_evidence_item') {
          conversationGroups[currentGroupIdx]?.push(
            <EmailPatientEvidenceBlock
              key={`email-patient-evidence-${entry.position}`}
              status={entry.status}
              patientEmail={entry.patientEmail}
              error={entry.error}
              needsEmail={entry.needsEmail}
              needsPdf={entry.needsPdf}
            />
          );
          continue;
        }

        // EVI-544: Handle medical calculator entries
        if (entry.type === 'calculator_item') {
          conversationGroups[currentGroupIdx]?.push(
            <CalculatorBlock
              key={`calculator-${entry.position}`}
              calculator={entry.calculator}
              status={entry.status}
              result={entry.result}
              error={entry.error}
            />
          );
          continue;
        }

        // EVI-620: Formula calculator block (dynamic calculator)
        if (entry.type === 'formula_calculator_item') {
          conversationGroups[currentGroupIdx]?.push(
            <FormulaCalculatorBlock
              key={`formula-calculator-${entry.position}`}
              formulaName={entry.formulaName}
              formulaExpression={entry.formulaExpression}
              formulaDisplay={entry.formulaDisplay}
              category={entry.category}
              status={entry.status}
              variables={entry.variables}
              result={entry.result}
              sourceUrl={entry.sourceUrl}
              sourceName={entry.sourceName}
              validationReference={entry.validationReference}
              criteriaBreakdown={entry.criteriaBreakdown}
              calculationSteps={entry.calculationSteps}
              warnings={entry.warnings}
              error={entry.error}
            />
          );
          continue;
        }

        // EVI-490: Handle clinical task result entries
        if (entry.type === 'clinical_task_result_item') {
          conversationGroups[currentGroupIdx]?.push(
            <ClinicalTaskResultBlock
              key={`clinical-task-result-${entry.position}`}
              status={entry.status}
              tasks={entry.tasks}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle sessions by date entries (Patients in a Day feature)
        if (entry.type === 'sessions_by_date_item') {
          conversationGroups[currentGroupIdx]?.push(
            <SessionsByDateBlock
              key={`sessions-by-date-${entry.position}`}
              status={entry.status}
              date={entry.date}
              totalSessions={entry.totalSessions}
              hasMore={entry.hasMore}
              sessions={entry.sessions}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle appointments entries
        if (entry.type === 'appointments_item') {
          conversationGroups[currentGroupIdx]?.push(
            <AppointmentsBlock
              key={`appointments-${entry.position}`}
              status={entry.status}
              date={entry.date}
              totalAppointments={entry.totalAppointments}
              appointments={entry.appointments}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle template search entries
        if (entry.type === 'search_templates_item') {
          conversationGroups[currentGroupIdx]?.push(
            <TemplateSearchBlock
              key={`search-templates-${entry.position}`}
              variant="own"
              status={entry.status}
              query={entry.query}
              totalResults={entry.totalResults}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle community template search entries
        if (entry.type === 'search_community_templates_item') {
          conversationGroups[currentGroupIdx]?.push(
            <TemplateSearchBlock
              key={`search-community-templates-${entry.position}`}
              variant="community"
              status={entry.status}
              query={entry.query}
              totalResults={entry.totalResults}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle user's own template search entries
        if (entry.type === 'search_my_templates_item') {
          conversationGroups[currentGroupIdx]?.push(
            <TemplateSearchBlock
              key={`search-my-templates-${entry.position}`}
              variant="own"
              status={entry.status}
              query={entry.query}
              totalResults={entry.totalResults}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle template & preferences fetch entries
        if (entry.type === 'get_template_prefs_item') {
          conversationGroups[currentGroupIdx]?.push(
            <TemplateSearchBlock
              key={`get-template-prefs-${entry.position}`}
              variant="own"
              purpose="fetch"
              status={entry.status === 'fetching' ? 'searching' : entry.status}
              templateAvailable={entry.templateAvailable}
              templateName={entry.templateName}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle create_template entries
        if (entry.type === 'create_template_item') {
          conversationGroups[currentGroupIdx]?.push(
            <CreateTemplateBlock
              key={`create-template-${entry.position}`}
              status={entry.status}
              templateId={entry.templateId}
              templateName={entry.templateName}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle edit_template_section entries
        if (entry.type === 'edit_template_section_item') {
          conversationGroups[currentGroupIdx]?.push(
            <EditTemplateSectionBlock
              key={`edit-template-section-${entry.position}`}
              status={entry.status}
              templateId={entry.templateId}
              templateName={entry.templateName}
              error={entry.error}
              toolId={entry.toolId}
            />
          );
          continue;
        }

        // Handle patient profile search entries
        if (entry.type === 'search_patient_profiles_item') {
          conversationGroups[currentGroupIdx]?.push(
            <PatientSearchBlock
              key={`search-patients-${entry.position}`}
              status={entry.status}
              query={entry.query}
              totalResults={entry.totalResults}
              error={entry.error}
            />
          );
          continue;
        }

        // Handle CSV export entries
        if (entry.type === 'csv_export_item') {
          conversationGroups[currentGroupIdx]?.push(
            renderSpreadsheetExportBlock(`artefact-${entry.position}`, entry)
          );
          continue;
        }

        // Handle Word document export entries
        if (entry.type === 'word_export_item') {
          conversationGroups[currentGroupIdx]?.push(
            <ArtefactBlock
              key={`artefact-${entry.position}`}
              artefactType="word"
              status={entry.status}
              title={entry.filename || 'Word Export'}
              url={entry.downloadUrl}
              error={entry.error}
              showMetadata={!!entry.sessionCount}
              metadataDescription={entry.sessionCount ? `${entry.sessionCount} sessions` : undefined}
            />
          );
          continue;
        }

        // Handle Excel spreadsheet export entries
        if (entry.type === 'excel_export_item') {
          conversationGroups[currentGroupIdx]?.push(
            renderSpreadsheetExportBlock(`artefact-${entry.position}`, entry)
          );
          continue;
        }

        // Handle PowerPoint presentation export entries
        if (entry.type === 'pptx_export_item') {
          conversationGroups[currentGroupIdx]?.push(
            renderPptxExportBlock(`artefact-${entry.position}`, entry)
          );
          continue;
        }

        // Unauthenticated tool access error (sign-up CTA)
        if (entry.type === 'unauthenticated_tool_access_error_item') {
          conversationGroups[currentGroupIdx]?.push(
            <UnauthenticatedToolAccessErrorBlock
              key={`unauthenticated-tool-error-${entry.position}`}
              message={entry.message}
              toolName={entry.toolName || undefined}
            />
          );
          continue;
        }

        // Tier-restricted tool error (upgrade CTA)
        if (entry.type === 'tier_restricted_tool_error_item') {
          conversationGroups[currentGroupIdx]?.push(
            <TierRestrictedToolBlock
              key={`tier-restricted-tool-error-${entry.position}`}
              message={entry.message}
              toolName={entry.toolName || undefined}
            />
          );
          continue;
        }

        if (entry.type === 'quota_exhausted_item') {
          conversationGroups[currentGroupIdx]?.push(
            <div key={`quota-exhausted-${entry.position}`} className="pb-5">
              <QuotaExceededMessage
                content={entry.message}
                upgradeUrl={entry.upgradeUrl}
              />
            </div>
          );
          continue;
        }

        // EHR tool calls get a dedicated expandable block with FHIR data
        if (entry.type === 'unsupported_tool_item' && entry.toolType.startsWith('ehr_')) {
          conversationGroups[currentGroupIdx]?.push(
            <EhrToolBlock
              key={`ehr-tool-${entry.position}`}
              toolType={entry.toolType}
              status={entry.status}
              resultData={entry.resultData}
              errorMessage={entry.errorMessage}
            />
          );
          continue;
        }

        // Fallback: unsupported tool call (desktop release behind web release)
        if (entry.type === 'unsupported_tool_item') {
          conversationGroups[currentGroupIdx]?.push(
            <UnsupportedToolBlock
              key={`unsupported-tool-${entry.position}`}
              toolType={entry.toolType}
              status={entry.status}
            />
          );
          continue;
        }

        // Handle text entries
        if (entry.type === 'text_item') {
          if (renderedTextPositions.has(entry.position)) continue;

          for (const [groupStart, groupEntries] of v2TextGroups) {
            if (groupEntries.some((e) => e.position === entry.position)) {
              groupEntries.forEach((e) => renderedTextPositions.add(e.position));
              renderTextGroup(
                groupStart,
                groupEntries,
                currentGroupIdx,
                conversationGroups,
                replyMetadata
              );
              break;
            }
          }
        }
      }

      // Show ProcessingBlock when the UI would otherwise appear frozen.
      // Covers two gaps:
      //   1. After thinking completes but before tool output arrives
      //   2. After a capability tool completes but before the next step starts
      const currentGroup = conversationGroups[currentGroupIdx];
      if (isStreaming && currentGroup) {
        const currentResponseEntries = timeline.filter(
          (e) => e.position >= (conversation.streamingState?.basePosition ?? 0)
        );
        const lastEntry = currentResponseEntries[currentResponseEntries.length - 1];

        const hasCompletedThinking =
          lastEntry?.type === 'thinking_item' && lastEntry.status === 'complete';
        const hasCompletedCapabilityTool =
          lastEntry?.type === 'capability_tool_item' && lastEntry.status === 'completed';
        const hasCompletedUnsupportedTool =
          lastEntry?.type === 'unsupported_tool_item' &&
          !['running', 'executing', 'searching', 'generating', 'fetching', 'calculating', 'creating', 'sending'].includes(
            (lastEntry.status || '').toLowerCase()
          );

        const hasToolOutputBlock = currentGroup.some(
          (el) =>
            el.key?.toString().startsWith('artefact-') ||
            el.key?.toString().startsWith('email-patient-evidence-') ||
            el.key?.toString().startsWith('clinical-task-result-') ||
            el.key?.toString().startsWith('sessions-by-date-') ||
            el.key?.toString().startsWith('appointments-') ||
            el.key?.toString().startsWith('search-templates-') ||
            el.key?.toString().startsWith('search-community-templates-') ||
            el.key?.toString().startsWith('search-patients-') ||
            el.key?.toString().startsWith('text-group-')
        );

        const needsProcessingBlock =
          (hasCompletedThinking && !hasToolOutputBlock) ||
          hasCompletedCapabilityTool ||
          hasCompletedUnsupportedTool;

        if (needsProcessingBlock) {
          // Detect the processing type based on user's query
          const lastUserMessage = timeline
            .slice()
            .reverse()
            .find((e) => e.type === 'user_message');
          const userQuery =
            lastUserMessage?.type === 'user_message'
              ? lastUserMessage.content.toLowerCase()
              : '';

          const hasFiles =
            lastUserMessage?.type === 'user_message' &&
            lastUserMessage.files &&
            lastUserMessage.files.length > 0;

          let processingType: ProcessingType =
            hasCompletedUnsupportedTool && hasFiles ? 'files' : 'general';

          if (processingType === 'general') {
            processingType = inferProcessingType(userQuery);
          }

          currentGroup.push(
            <ProcessingBlock
              key={`processing-${lastEntry.position}`}
              type={processingType}
            />
          );
        }
      }

      // Show slow-stream hint when visible content hasn't appeared for 30s
      if (isStreaming && conversation.streamingState?.streamSlow) {
        conversationGroups[currentGroupIdx]?.push(
          <StreamSlowHint key="stream-slow-hint" />
        );
      }

      // Add error message if exists
      const lastError = timeline.findLast(
        (entry) => entry && entry.type && entry.type === 'error_item'
      );
      if (lastError) {
        const isRateLimitError = lastError.content === TIMELINE_STREAM_ERROR.RATE_LIMIT_EXCEEDED;
        const isUserRefBannedError =
          lastError.content === TIMELINE_STREAM_ERROR.USER_REF_BANNED;
        conversationGroups[currentGroupIdx]?.push(
          isRateLimitError ? (
            <RateLimitErrorBlock
              key={`error-${lastError.id}`}
              onRetry={onRetryLastMessage}
              cooldownUntil={rateLimitCooldownUntil}
            />
          ) : isUserRefBannedError ? (
            <UserRefBannedBlock
              key={`error-${lastError.id}`}
              isAuthenticated={isAuthenticated}
            />
          ) : (
            <ComplianceErrorMessage
              key={`error-${lastError.id}`}
              message={lastError.content}
              onRetry={onRetryLastMessage}
            />
          )
        );
      }

      return buildFinalElements(conversationGroups, replyMetadata);
    };

    return (
      // The ONE host on this path. Every surface that reads
      // `EvidenceAnswerPorts` here — the search, library and print blocks, the
      // sources rollup and `TextResponse`'s message body — renders below it.
      <EvidenceAnswerPortsHost>
      <div className="relative h-full">
        <ScrollArea ref={ref} viewportRef={viewportRefCallback} className="h-full" viewportClassName="[&>div]:!block">
          <div
            ref={timelineRef}
            className={cn(
              'flex min-h-full flex-col items-center space-y-4',
              !isFullScreenEvidence && 'px-5'
            )}
          >
            <div
              className={cn(
                'w-full min-w-0 max-w-3xl overflow-x-hidden [&>:first-child]:pt-2 [&>:last-child]:pb-16 sm:[&>:last-child]:pb-24',
                // Direct children here are whole turns, so this is the
                // exchange-to-exchange tier — the largest gap in the thread.
                responseColumnClass(density),
                // EVI-1602: keep the larger desktop top padding for normal
                // chats, but drop it during the SBS comparison so the query
                // title hugs the top and the gap to the overlay heading shrinks.
                !isSBSActive && 'sm:[&>:first-child]:pt-4',
                isFullScreenEvidence && 'px-4'
              )}
            >
              {renderV2Timeline()}
            </div>
          </div>
        </ScrollArea>
        {/* Bottom fade gradient overlay - creates smooth fade effect as content scrolls behind input */}
        {/* Uses surface-shell for inset design, surface-page for full-screen evidence, surface-container for in-session sidebar */}
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t sm:h-24',
            IS_INSET_DESIGN_ENABLED() ? 'from-surface-shell to-surface-shell/0' : isFullScreenEvidence ? 'from-surface-page to-surface-page/0' : 'from-surface-container to-surface-container/0'
          )}
          aria-hidden="true"
        />
        {/* Position indicator centered with content area */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center">
          <div className="w-full max-w-3xl">
            <ScrollDownIndicator
              isVisible={hasMoreContentBelow}
              onClick={onScrollToBottom ?? (() => {})}
              className="relative"
            />
          </div>
        </div>
        {/* Quick navigation to answered questions - desktop only */}
        {!isMobile && (
          <ChatQuestionNav
            timeline={conversation.timeline}
            scrollAreaRef={ref as React.RefObject<HTMLDivElement>}
            isStreaming={isStreaming}
          />
        )}
      </div>
      </EvidenceAnswerPortsHost>
    );
  })
);

ChatTimeline.displayName = 'ChatTimeline';
